import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, FileText, Mail, Calendar, ChevronUp } from 'lucide-react';
import { LOGO_URL, BrandText } from '@/lib/brand';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background" data-testid="terms-page">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 border-b border-border/40" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.97) 0%, rgba(248,250,252,0.97) 100%)', backdropFilter: 'blur(20px) saturate(180%)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
          <Link to="/" className="flex items-center gap-2.5"><img src={LOGO_URL} alt="Out-Play" className="h-9 w-9 rounded-xl object-cover" /><BrandText className="text-xl tracking-tight" /></Link>
          <Link to="/"><Button variant="ghost" size="sm" className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Back to Home</Button></Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.1),transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 text-center">
          <FileText className="h-12 w-12 text-blue-400 mx-auto mb-4" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-white mb-3">Terms of Service</h1>
          <p className="text-slate-400 text-lg max-w-xl mx-auto">Rules and guidelines for using the Out-Play.in platform</p>
          <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 mt-4"><Calendar className="h-3 w-3 mr-1" /> Last Updated: February 19, 2026</Badge>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="space-y-10">
          <div className="p-6 rounded-2xl bg-card border shadow-sm">
            <p className="text-base leading-relaxed text-muted-foreground">These Terms of Service ("Terms") govern your use of <strong className="text-foreground">Out-Play.in</strong>, a sports academy management platform. By creating an account or using our services, you agree to these Terms. If you do not agree, please do not use our platform.</p>
          </div>

          <div><h2 className="text-xl font-display font-bold mb-4">1. Account Registration</h2>
            <div className="text-base text-muted-foreground leading-relaxed space-y-3">
              <ul className="list-disc pl-5 space-y-2">
                <li>You must provide accurate and complete information during registration.</li>
                <li>You are responsible for maintaining the security of your account credentials.</li>
                <li>Academy owners are responsible for managing access for their coaches, staff, and students.</li>
                <li>You must be at least 18 years old to create an academy owner account. Student accounts require parental consent for minors.</li>
              </ul>
            </div>
          </div>
          <Separator />

          <div><h2 className="text-xl font-display font-bold mb-4">2. Acceptable Use</h2>
            <div className="text-base text-muted-foreground leading-relaxed space-y-3">
              <p>You agree to use Out-Play.in only for lawful sports academy management purposes. You shall not:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Upload false, misleading, or fraudulent information.</li>
                <li>Attempt to access another academy's data or bypass security measures.</li>
                <li>Use the platform for any purpose other than sports academy management.</li>
                <li>Reverse engineer, decompile, or attempt to extract source code.</li>
                <li>Resell or redistribute access to the platform without authorization.</li>
              </ul>
            </div>
          </div>
          <Separator />

          <div><h2 className="text-xl font-display font-bold mb-4">3. Subscription & Payments</h2>
            <div className="text-base text-muted-foreground leading-relaxed space-y-3">
              <ul className="list-disc pl-5 space-y-2">
                <li><strong className="text-foreground">Free Trial:</strong> 14-day trial with full access. No credit card required.</li>
                <li><strong className="text-foreground">Pro Plan (₹999/month):</strong> Billed monthly. Cancel anytime — access continues until end of billing period.</li>
                <li><strong className="text-foreground">Enterprise:</strong> Custom pricing with dedicated support. Contact sales for details.</li>
                <li>All payments are processed securely via Razorpay. Prices are in Indian Rupees (INR) and include applicable GST.</li>
                <li>Refunds may be issued at our discretion within 7 days of payment for unused services.</li>
              </ul>
            </div>
          </div>
          <Separator />

          <div><h2 className="text-xl font-display font-bold mb-4">4. Data Ownership</h2>
            <div className="text-base text-muted-foreground leading-relaxed space-y-3">
              <p>You retain full ownership of all data you upload to Out-Play.in, including student records, attendance data, financial records, and media files. We do not claim ownership of your content. You grant us a limited license to process, store, and display your data solely for the purpose of providing our services.</p>
            </div>
          </div>
          <Separator />

          <div><h2 className="text-xl font-display font-bold mb-4">5. Service Availability</h2>
            <div className="text-base text-muted-foreground leading-relaxed space-y-3">
              <p>We strive for 99.9% uptime but do not guarantee uninterrupted service. We may perform maintenance with advance notice. We are not liable for downtime caused by third-party services, internet outages, or force majeure events.</p>
            </div>
          </div>
          <Separator />

          <div><h2 className="text-xl font-display font-bold mb-4">6. Termination</h2>
            <div className="text-base text-muted-foreground leading-relaxed space-y-3">
              <ul className="list-disc pl-5 space-y-2">
                <li>You may cancel your subscription and delete your account at any time from Settings.</li>
                <li>We may suspend or terminate accounts that violate these Terms.</li>
                <li>Upon termination, your data will be retained for 30 days before permanent deletion, allowing you to export your records.</li>
              </ul>
            </div>
          </div>
          <Separator />

          <div><h2 className="text-xl font-display font-bold mb-4">7. Limitation of Liability</h2>
            <div className="text-base text-muted-foreground leading-relaxed">
              <p>Out-Play.in is provided "as is" without warranties of any kind. Our total liability is limited to the amount you paid for the service in the 12 months preceding the claim. We are not liable for indirect, incidental, or consequential damages.</p>
            </div>
          </div>
          <Separator />

          <div><h2 className="text-xl font-display font-bold mb-4">8. Governing Law</h2>
            <div className="text-base text-muted-foreground leading-relaxed">
              <p>These Terms are governed by the laws of India. Any disputes shall be resolved through arbitration in Vijayawada, Andhra Pradesh, India, under the Arbitration and Conciliation Act, 1996.</p>
            </div>
          </div>
          <Separator />

          <div><h2 className="text-xl font-display font-bold mb-4">9. Contact</h2>
            <div className="p-6 rounded-2xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
              <p className="text-base text-muted-foreground mb-3">For questions about these Terms, please contact us:</p>
              <div className="flex items-center gap-2 text-base"><Mail className="h-5 w-5 text-blue-600" /><a href="mailto:contact@out-play.in" className="text-primary font-semibold hover:underline">contact@out-play.in</a></div>
            </div>
          </div>
        </div>
      </section>

      <div className="text-center py-8"><button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"><ChevronUp className="h-4 w-4" /> Back to top</button></div>

      <footer className="border-t border-border bg-card py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className="flex items-center gap-2"><img src={LOGO_URL} alt="Out-Play" className="h-6 w-6 rounded object-cover" /><BrandText className="text-sm" /><span className="text-xs text-muted-foreground">&copy; 2026</span></div>
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <span className="text-foreground font-medium">Terms of Service</span>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
